import type { PaintingRecord } from "../types";
import { escapeHtml } from "../util/html";
import { deferForFastPaint } from "../util/defer";
import { fetchJson } from "../util/fetch-json";
import { resolveAudioUrl } from "../util/audio";
import {
  buildPaintingPairings,
  type PaintingWithTracks,
  type TrackMatch,
} from "../util/art-music-pairing";
import {
  paintingCardImageCandidates,
  wirePaintingImgFallbacks,
} from "../util/painting-images";

const TRACKS_URL = "/data/tracks-local.json";
const PAINTINGS_URL = "/data/paintings.json";

function paintingImgTag(p: PaintingRecord): string {
  const urls = paintingCardImageCandidates(p);
  const enc = encodeURIComponent(JSON.stringify(urls));
  const alt = escapeHtml(p.title);
  return `<img class="pair-painting-img art-img-with-fallback" src="${escapeHtml(urls[0] || "")}" alt="${alt}" loading="lazy" decoding="async" data-pfb="${enc}" />`;
}

function trackRow({ track, reason }: TrackMatch): string {
  const meta = [
    track.composer,
    normalizeEraLabel(track.era),
    track.year != null ? String(track.year) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const src = track.audioFile ? escapeHtml(resolveAudioUrl(track.audioFile)) : "";
  const player = src
    ? `<audio controls preload="none" src="${src}" class="pair-track-audio"></audio>`
    : "";
  return `
    <li class="pair-track-item">
      <div class="pair-track-top">
        <a class="pair-track-main" href="/music?track=${track.id}">
          <span class="pair-track-title">${escapeHtml(track.title)}</span>
          <span class="pair-track-meta">${escapeHtml(meta)}</span>
        </a>
        ${player}
      </div>
      <p class="pair-track-reason">${escapeHtml(reason)}</p>
    </li>
  `;
}

function normalizeEraLabel(era: string | undefined): string {
  const e = (era || "").trim();
  if (e === "古典主义/浪漫主义") return "古典主义";
  return e;
}

function pairCard({ painting, matches }: PaintingWithTracks): string {
  const py =
    typeof painting.year === "number"
      ? String(painting.year)
      : painting.year
        ? escapeHtml(String(painting.year))
        : "";
  const headMeta = [escapeHtml(painting.artist), escapeHtml(painting.era), py]
    .filter(Boolean)
    .join(" · ");

  return `
    <article class="pair-card" id="pair-p-${painting.id}">
      <div class="pair-card-visual">
        <a href="/art?painting=${painting.id}" class="pair-painting-link" aria-label="查看《${escapeHtml(painting.title)}》详情">
          ${paintingImgTag(painting)}
        </a>
      </div>
      <div class="pair-card-body">
        <header class="pair-card-head">
          <h2 class="pair-card-title">
            <a href="/art?painting=${painting.id}">${escapeHtml(painting.title)}</a>
          </h2>
          <p class="pair-card-meta">${headMeta}</p>
          ${painting.museum ? `<p class="pair-card-museum section-sub">${escapeHtml(painting.museum)}</p>` : ""}
        </header>
        <section class="pair-tracks" aria-labelledby="pair-tracks-${painting.id}">
          <h3 class="pair-tracks-label" id="pair-tracks-${painting.id}">相配名曲（${matches.length} 首）</h3>
          <ol class="pair-track-list">
            ${matches.map(trackRow).join("")}
          </ol>
        </section>
      </div>
    </article>
  `;
}

function eraFilterRow(eras: string[], active: string): string {
  const chips = eras
    .map(
      (era) =>
        `<button type="button" class="filter-chip ${active === era ? "active" : ""}" data-pair-era="${escapeHtml(era)}">${escapeHtml(era)}</button>`
    )
    .join("");
  return `<div class="filter-row pair-era-filters" role="tablist" aria-label="按画作时期筛选">${chips}</div>`;
}

function listHtml(pairings: PaintingWithTracks[], era: string, skippedModern: number): string {
  const filtered =
    era === "全部" ? pairings : pairings.filter((x) => x.painting.era === era);
  const eras = ["全部", ...Array.from(new Set(pairings.map((x) => x.painting.era))).sort()];

  return `
    <header class="pair-page-head">
      <h1 class="section-title" style="margin-top:0">对对配馆</h1>
      <p class="section-sub pair-page-lead">
        以馆藏名画为主角，每幅精选 <strong>1～2</strong> 首曲库中真实可播、且与画作气质相符的名曲，并说明画境与乐境的呼应。
        当前 <strong>${pairings.length}</strong> 幅画作已配对；
        ${skippedModern > 0 ? `另有 ${skippedModern} 幅现代艺术画作待扩充曲库后再配。` : ""}
      </p>
    </header>
    ${eraFilterRow(eras, era)}
    <div class="pair-gallery" id="pair-gallery">
      ${filtered.length ? filtered.map(pairCard).join("") : '<p class="section-sub">该时期暂无配对画作。</p>'}
    </div>
  `;
}

export function renderPair(): string {
  return `
    <div id="pair-root" class="pair-page">
      <p class="section-sub" style="margin-top:0">正在加载名画与曲库…</p>
    </div>
  `;
}

export function hydratePair(container: HTMLElement): void {
  deferForFastPaint(() => void hydratePairAsync(container));
}

async function hydratePairAsync(container: HTMLElement): Promise<void> {
  const root = container.querySelector("#pair-root");
  if (!root) return;

  try {
    const [paintingsPayload, tracksPayload] = await Promise.all([
      fetchJson<{ paintings: PaintingRecord[] }>(PAINTINGS_URL, "名画目录"),
      fetchJson<{ tracks: TrackRecord[] }>(TRACKS_URL, "曲库"),
    ]);
    if (!root.isConnected) return;

    const paintings = paintingsPayload.paintings ?? [];
    const tracks = tracksPayload.tracks ?? [];
    const pairings = buildPaintingPairings(paintings, tracks);
    const skippedModern = paintings.filter((p) => p.era === "现代艺术").length;

    let activeEra = sessionStorage.getItem("sanoi-pair-era") || "全部";
    if (activeEra !== "全部" && !pairings.some((x) => x.painting.era === activeEra)) {
      activeEra = "全部";
    }

    const render = (era: string) => {
      if (!root.isConnected) return;
      root.innerHTML = listHtml(pairings, era, skippedModern);
      sessionStorage.setItem("sanoi-pair-era", era);
      wirePaintingImgFallbacks(root);
      root.querySelectorAll<HTMLButtonElement>(".filter-chip[data-pair-era]").forEach((btn) => {
        btn.addEventListener("click", () => {
          render(btn.getAttribute("data-pair-era") || "全部");
        });
      });
      const audios = [...root.querySelectorAll<HTMLAudioElement>(".pair-track-audio")];
      for (const current of audios) {
        current.addEventListener("play", () => {
          for (const other of audios) {
            if (other !== current) other.pause();
          }
        });
      }
    };

    render(activeEra);
  } catch (e) {
    if (!root.isConnected) return;
    const msg = e instanceof Error ? e.message : "内容暂时无法加载";
    root.innerHTML = `<p class="section-sub">${escapeHtml(msg)}。请确认已部署 <code>/data/paintings.json</code> 与 <code>/data/tracks-local.json</code>。</p>`;
  }
}
